import type {
  RuntimeDiagnosticsResponse,
  ServerTraceDiagnosticsResult,
} from "@cmmd-forge/contracts";

import { thisServerInventoryCopy } from "./cloudEnvironmentInventory.logic";

import { DiagnosticsEvidenceNotice } from "./DiagnosticsEvidenceNotice";
import {
  DiagnosticsCard,
  DiagnosticsMetrics,
  DiagnosticsNote,
  DiagnosticsSection,
  StatusDot,
  StatusPill,
  formatTimestamp,
  type DiagnosticsMetric,
} from "./DiagnosticsPanelPrimitives";
import { DiagnosticsProcessesTable } from "./DiagnosticsProcessesTable";
import { DiagnosticsTraceFailures } from "./DiagnosticsTraceFailures";
import {
  filterRuntimeInventoryServices,
  summarizeRuntimeTools,
} from "./DiagnosticsSettingsPanel.logic";
import type { StatusTone } from "./DiagnosticsSettings.types";

function formatUptime(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(total / 3_600);
  const minutes = Math.floor((total % 3_600) / 60);
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  if (minutes > 0) return `${minutes}m ${String(total % 60).padStart(2, "0")}s`;
  return `${total}s`;
}

function localServerMetrics(
  inventory: RuntimeDiagnosticsResponse,
): ReadonlyArray<DiagnosticsMetric> {
  const currentProcess = inventory.currentProcess;
  return [
    {
      label: "Build",
      value:
        inventory.runtime.sandboxRuntimeVersion ??
        inventory.runtime.commit ??
        inventory.runtime.nodeEnv ??
        "Not reported",
      mono: true,
    },
    { label: "Machine", value: inventory.machineId, mono: true },
    { label: "Read", value: formatTimestamp(inventory.fetchedAt) },
    ...(currentProcess
      ? [
          {
            label: "Platform",
            value: `${currentProcess.platform}/${currentProcess.arch} · Node ${currentProcess.nodeVersion}`,
          },
          { label: "Uptime", value: formatUptime(currentProcess.uptimeSeconds) },
          {
            label: "Working directory",
            value: currentProcess.cwd ?? "Not reported",
            mono: true,
          },
        ]
      : []),
  ];
}

function toolTone(status: "ok" | "error"): StatusTone {
  return status === "ok" ? "ready" : "error";
}

function ToolsAndServices({ inventory }: { readonly inventory: RuntimeDiagnosticsResponse }) {
  const services = filterRuntimeInventoryServices(inventory.localServices ?? [], inventory);
  const brokenTools = inventory.tools.filter((tool) => tool.status === "error");
  if (inventory.tools.length === 0 && services.length === 0) return null;
  return (
    <DiagnosticsSection
      title="Tools and services"
      id="diagnostics-tools"
      description={summarizeRuntimeTools(inventory.tools)}
      action={
        brokenTools.length > 0 ? (
          <StatusPill label={`${brokenTools.length} failing`} tone="error" />
        ) : null
      }
    >
      <DiagnosticsCard>
        {inventory.tools.length > 0 ? (
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 border-b border-border/65 px-4 py-3 last:border-b-0">
            {inventory.tools.map((tool) => (
              <span key={tool.name} className="inline-flex items-center gap-1.5 text-xs">
                <StatusDot tone={toolTone(tool.status)} />
                <span className="text-foreground">{tool.name}</span>
              </span>
            ))}
          </div>
        ) : null}
        {/* A failing tool's own words are the diagnosis; a green/red dot is not. */}
        {brokenTools.map((tool) => (
          <p
            key={`${tool.name}-error`}
            className="border-b border-border/65 px-4 py-2 font-mono text-[11px] text-destructive-foreground last:border-b-0"
          >
            {tool.name}: {tool.error ?? tool.output ?? "failed with no output"}
          </p>
        ))}
        {services.map((service) => (
          <div
            key={service.name}
            className="flex items-baseline gap-3 border-b border-border/65 px-4 py-2 last:border-b-0"
          >
            <StatusDot tone={service.status === "configured" ? "ready" : "muted"} />
            <span className="text-xs text-foreground">{service.name}</span>
            <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-muted-foreground">
              {service.url ?? service.port ?? service.detail ?? service.status}
            </span>
          </div>
        ))}
      </DiagnosticsCard>
    </DiagnosticsSection>
  );
}

/**
 * The server serving this page — the one diagnostics target Forge can read
 * first-hand. Its evidence used to live on Settings → System, where the page
 * that exists to answer "what is wrong with this machine" could not reach it;
 * `runtimeDiagnosticsRoute.ts`'s own header comment says the SPA's Diagnostics
 * panel is the consumer, which is now true.
 */
export function DiagnosticsLocalServer({
  label,
  inventory,
  inventoryError,
  traces,
  tracesError,
  pending,
  probed,
}: {
  readonly label: string;
  readonly inventory: RuntimeDiagnosticsResponse | undefined;
  readonly inventoryError: string | undefined;
  readonly traces: ServerTraceDiagnosticsResult | undefined;
  readonly tracesError: string | undefined;
  readonly pending: boolean;
  readonly probed: boolean;
}) {
  return (
    <>
      <DiagnosticsSection
        title={label}
        id="diagnostics-target"
        description={thisServerInventoryCopy()}
        action={<StatusPill label="connected" tone="ready" />}
      >
        <div className="px-3 pb-2 sm:px-4">
          <DiagnosticsEvidenceNotice evidence="direct" />
        </div>
        <DiagnosticsCard>
          {inventory ? (
            <DiagnosticsMetrics metrics={localServerMetrics(inventory)} />
          ) : (
            <DiagnosticsNote>
              {pending
                ? "Reading this server's runtime inventory…"
                : (inventoryError ??
                  "This server did not return a runtime inventory. Tracing below may still be readable.")}
            </DiagnosticsNote>
          )}
        </DiagnosticsCard>
      </DiagnosticsSection>

      {probed ? (
        <DiagnosticsTraceFailures result={traces} error={tracesError} pending={pending} />
      ) : null}

      {inventory ? (
        <DiagnosticsSection
          title="Processes"
          id="diagnostics-processes"
          description={`${inventory.processes.length} in this server's tree, deepest last.`}
        >
          <DiagnosticsCard>
            <DiagnosticsProcessesTable
              processes={inventory.processes}
              rootPid={inventory.currentProcess?.pid ?? null}
              collectionError={inventory.processCollectionError}
            />
          </DiagnosticsCard>
        </DiagnosticsSection>
      ) : null}

      {inventory ? <ToolsAndServices inventory={inventory} /> : null}
    </>
  );
}
